import { useEffect, useRef } from 'react';
import { useRouter } from '@tanstack/react-router';

import { useTranslation } from 'react-i18next';
import { UnsavedChangesModal } from '@/common/components/UnsavedChangesModal';
import { createRoot } from 'react-dom/client';

/**
 * Hook to block navigation when there are unsaved changes
 * This approach intercepts all navigation attempts including:
 * - Link clicks
 * - router.navigate() calls
 * - Browser back/forward/refresh
 */
export function useNavigationBlocker(isDirty: boolean, message?: string) {
    const router = useRouter();
    const { t } = useTranslation();
    const originalNavigateRef = useRef(router.navigate.bind(router));

    useEffect(() => {
        // Capture the original navigate at the start of the effect
        const originalNavigate = originalNavigateRef.current;
        
        if (!isDirty) {
            // Restore original navigate if not dirty
            router.navigate = originalNavigate;
            return;
        }

        // Helper function to show custom modal using imperative API
        const showConfirmModal = (onConfirm: () => void) => {
            let isOpen = true;
            
            const container = document.createElement('div');
            document.body.appendChild(container);
            const root = createRoot(container);

            const handleStay = () => {
                isOpen = false;
                root.unmount();
                document.body.removeChild(container);
            };

            const handleLeave = () => {
                isOpen = false;
                root.unmount();
                document.body.removeChild(container);
                onConfirm();
            };

            root.render(
                <UnsavedChangesModal
                    open={isOpen}
                    onStay={handleStay}
                    onLeave={handleLeave}
                    message={message}
                />
            );
        };

        // Override router.navigate to show confirmation
        const interceptedNavigate = (async (options: unknown) => {
            const opts = options as { skipBlock?: boolean } & Parameters<typeof originalNavigate>[0];
            if (opts?.skipBlock) {
                await originalNavigate(options as Parameters<typeof originalNavigate>[0]);
                return;
            }

            return new Promise<void>((resolve) => {
                showConfirmModal(async () => {
                    await originalNavigate(options as Parameters<typeof originalNavigate>[0]);
                    resolve();
                });
            });
        }) as typeof router.navigate;

        router.navigate = interceptedNavigate;

        // Block browser back/forward/refresh
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
            return '';
        };

        // Block link clicks
        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const link = target.closest('a');
            
            if (!link) return;
            
            // Only intercept internal navigation links
            const href = link.getAttribute('href');
            if (!href || href.startsWith('http') || href.startsWith('mailto:')) return;

            e.preventDefault();
            e.stopPropagation();

            showConfirmModal(() => {
                const targetPath = link.getAttribute('href');
                if (targetPath) {
                    originalNavigate({ to: targetPath } as Parameters<typeof originalNavigate>[0]);
                }
            });
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('click', handleClick, true); // Use capture phase

        return () => {
            // Cleanup: restore original navigate
            router.navigate = originalNavigate;
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('click', handleClick, true);
        };
    }, [isDirty, message, router, t]);
}
